import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';

export const EmailTestComponent: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = `[${timestamp}] ${message}`;
    setTestResults(prev => [...prev, logMessage]);
  };

  const clearLogs = () => {
    setTestResults([]);
  };

  const testEmailFunction = async () => {
    setIsLoading(true);
    clearLogs();
    
    try {
      addLog('🔧 Iniciando teste da edge function send-email...');
      
      // Get current user
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        addLog(`❌ Erro ao buscar usuário: ${userError.message}`, 'error');
        return;
      }
      
      if (!userData.user?.email) {
        addLog('❌ Email do usuário não encontrado', 'error');
        return;
      }
      
      addLog(`👤 Usuário logado: ${userData.user.email}`);
      
      // Test payload
      const testPayload = {
        to: userData.user.email,
        template: 'mentorshipCreated',
        data: {
          title: 'Teste de Email - Mentoria Criada',
          gameName: 'Pokemon TCG',
          durationMinutes: 60,
          priceCents: 5000,
          appUrl: window.location.origin
        }
      };
      
      addLog(`📤 Enviando teste com payload: ${JSON.stringify(testPayload, null, 2)}`);
      
      // Call edge function
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: testPayload
      });
      
      if (error) {
        addLog(`❌ Erro na edge function: ${error.message}`, 'error');
        addLog(`🔍 Detalhes do erro: ${JSON.stringify(error, null, 2)}`, 'error');
      } else {
        addLog(`✅ Edge function executada com sucesso!`, 'success');
        addLog(`📋 Resposta completa: ${JSON.stringify(data, null, 2)}`, 'success');
        
        // Verificar se há detalhes específicos na resposta
        if (data && data.success) {
          addLog(`🎯 Status: SUCCESS - Email processado pela edge function`, 'success');
        }
        if (data && data.messageId) {
          addLog(`📧 Message ID: ${data.messageId}`, 'success');
        }
        if (data && data.error) {
          addLog(`⚠️ Erro interno: ${data.error}`, 'error');
        }
      }
      
    } catch (error: any) {
      addLog(`💥 Erro inesperado: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const testMentorshipCreation = async () => {
    setIsLoading(true);
    clearLogs();
    
    try {
      addLog('🎮 Simulando criação de mentoria...');
      
      // Import notification service
      const { notifyMentorshipCreation } = await import('@/services/mentorOfferings/notificationService');
      
      const testOfferingData = {
        title: 'Mentoria de Teste - Email Debug',
        description: 'Mentoria criada para testar envio de email',
        price_cents: 5000,
        duration_minutes: 60,
        game_id: 'f6f1f0b2-b758-4d1e-87a6-d4f7f9884c05', // Pokemon TCG
        is_group: false,
        max_participants: 1
      };
      
      addLog(`📊 Dados da mentoria: ${JSON.stringify(testOfferingData, null, 2)}`);
      addLog('🔔 Chamando notifyMentorshipCreation...');
      
      await notifyMentorshipCreation(testOfferingData);
      
      addLog('✅ Função de notificação executada! Verifique os logs do notificationService no console.', 'success');
      
    } catch (error: any) {
      addLog(`💥 Erro no teste: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const checkSupabaseLogs = async () => {
    setIsLoading(true);
    clearLogs();
    
    try {
      addLog('📊 Buscando logs da edge function no Supabase...');
      
      // Get project info
      addLog('🔍 Esta função requer acesso aos logs do Supabase');
      addLog('💡 Para verificar logs detalhados:');
      addLog('1. Acesse https://supabase.com/dashboard');
      addLog('2. Selecione seu projeto');
      addLog('3. Vá em "Edge Functions" > "Logs"');
      addLog('4. Filtre por "send-email"');
      addLog('5. Verifique os logs dos últimos minutos');
      
      addLog('🎯 Procure por:', 'info');
      addLog('- Erros de API key do Resend', 'info');
      addLog('- Problemas de template', 'info');
      addLog('- Respostas da API do Resend', 'info');
      
    } catch (error: any) {
      addLog(`💥 Erro: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="bg-[#1A2233] border-[#222A35] max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          🔧 Teste de Email - Debug Avançado
          <Badge variant="outline" className="text-[#00B6F0] border-[#00B6F0]">
            v2.0
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={testEmailFunction}
            disabled={isLoading}
            className="bg-[#00B6F0] text-black hover:bg-[#00A3D9]"
          >
            {isLoading ? 'Testando...' : 'Testar Edge Function Direta'}
          </Button>
          
          <Button 
            onClick={testMentorshipCreation}
            disabled={isLoading}
            className="bg-[#51EFFF] text-black hover:bg-[#45D4E8]"
          >
            {isLoading ? 'Testando...' : 'Testar Notificação Mentoria'}
          </Button>
          
          <Button 
            onClick={checkSupabaseLogs}
            disabled={isLoading}
            className="bg-[#8B5CF6] text-white hover:bg-[#7C3AED]"
          >
            {isLoading ? 'Verificando...' : 'Como Ver Logs Supabase'}
          </Button>
          
          <Button 
            onClick={clearLogs}
            variant="outline"
            className="border-[#2A3749] text-white hover:bg-[#242C37]"
          >
            Limpar Logs
          </Button>
        </div>
        
        <div className="bg-[#0F1419] border border-[#2D3748] rounded-lg p-4 max-h-96 overflow-y-auto">
          <div className="text-[#00B6F0] font-bold mb-2">📋 Logs de Teste:</div>
          {testResults.length === 0 ? (
            <div className="text-gray-400 italic">Nenhum teste executado ainda...</div>
          ) : (
            testResults.map((result, index) => (
              <div key={index} className="text-sm font-mono text-gray-300 mb-1 whitespace-pre-wrap">
                {result}
              </div>
            ))
          )}
        </div>
        
        <div className="text-sm text-gray-400 space-y-2">
          <div>💡 <strong>Dica:</strong> Se a edge function retorna sucesso mas o email não chega:</div>
          <ul className="ml-4 space-y-1">
            <li>• Verifique SPAM/Lixo Eletrônico</li>
            <li>• Verifique abas Promoções/Social no Gmail</li>
            <li>• Verifique logs do Supabase para erros internos</li>
            <li>• Confirme se a API key do Resend está correta</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}; 